import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import StyledInput from '../styled/StyledInput';
import { Label } from '../ui/label';
import StyledRadioGroup from '../styled/StyledRadioGroup';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledButton from '../styled/StyledButton';
import StyledBooleanCheckbox from '../styled/StyledBooleanCheckbox';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import { useTranslateFields } from '@/hooks/useTranslate';
import TranslateIcon from '../translation/TranslateIcon';

interface TaxesListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const TaxesList = ({
  control,
  language,
  isViewMode = false,
}: TaxesListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'taxes',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'taxes',
    });

  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();
  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [],
    nestedFields: {
      taxes: ['name'],
    },
    targetLanguage: language,
  });
  return (
    <StyledFormSection
      sectionTitle={t('carRental.categoryForm.addTax')}
      icon={
        <TranslateIcon
          translateFields={() => translateFields({ allNestedFields: true })}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      }
    >
      {fields.map((item, index) => (
        <TaxCard
          key={item.id}
          control={control}
          index={index}
          language={language}
          deleteTaxes={() => {
            remove(index);
            removeTranslation(index);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addTax')}
          onClick={() => {
            append({
              name: undefined,
              amount: undefined,
              billingType: undefined,
              required: false,
            } as CarRentalFormDataWithTranslations['taxes'][number]);
            appendTranslation(() => ({
              name: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </StyledFormSection>
  );
};

export default TaxesList;

interface TaxesCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  deleteTaxes: () => void;
  isViewMode?: boolean;
}

const TaxCard = ({
  control,
  index,
  language,
  deleteTaxes,
  isViewMode = false,
}: TaxesCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `taxes.${index}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation = translations?.[language]?.taxes?.[index];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.tax', { index: index + 1 })
    );
  }, [currentTitle, index, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {index > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={deleteTaxes}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`taxes.${index}.name`}
            control={control}
            title={t('carRental.categoryForm.name')}
            name={`taxes.${index}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.taxNamePlaceholder')}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex flex-col md:flex-row md:justify-between">
              <div className="flex gap-6 w-full items-center">
                <div className="min-w-20 max-w-40">
                  <StyledInput
                    control={control}
                    name={`taxes.${index}.amount`}
                    placeholder={t('carRental.categoryForm.amountPlaceholder')}
                    type="number"
                    min={0}
                    disabled={isViewMode}
                  />
                </div>
                <StyledRadioGroup
                  control={control}
                  name={`taxes.${index}.billingType`}
                  options={[
                    {
                      label: t('carRental.categoryForm.billingTypePerDay'),
                      value: 'perDay',
                    },
                    {
                      label: t('carRental.categoryForm.billingTypePerRental'),
                      value: 'perRental',
                    },
                  ]}
                  className="w-1/2"
                  disabled={isViewMode}
                />
              </div>
              <StyledBooleanCheckbox
                control={control}
                name={`taxes.${index}.required`}
                optionLabel={t('carRental.categoryForm.required')}
                className="flex items-center"
                disabled={isViewMode}
              />
            </div>
            <p className="text-sm text-gray-500">
              {t('carRental.globalFormDescription')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
